import { MeetingSessionOvenMediaReceiverManagerBaseData, useOvenMediaVideoSubscriptions } from "@kalyzee/kast-web-sdk";
import { AddEventListernerResult, useEventListenersRef, useRefAutoUpdate, WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { OvenMediaSessionService } from "../../helpers/ovenmedia";

import { toastError, toastSuccess } from "../../helpers/toast";
import { useSettings } from "../../hooks/settings";
import { WebrtcSessionStats } from "../debug/WebrtcSessionStats";
import { PlayerContainerRenderer } from "./interfaces";
import MeetingSessionMediaStreamPlayer, { MeetingSessionMediaStreamPlayerEventListenerMap, MeetingSessionMediaStreamPlayerRef } from "./MeetingSessionMediaStreamPlayer";
import { ReactZoomPanPinchProps } from "react-zoom-pan-pinch";

export interface MeetingSessionMediaPlayerEventListenerMap extends MeetingSessionMediaStreamPlayerEventListenerMap {}

export interface MeetingSessionMediaPlayerProps extends WithNativeHTMLProps<HTMLDivElement> {
  meeting: Meeting;
  sessionId: string;
  mediaSessionId?: string;
  ovenMediaSession?: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>;
  muted?: boolean;
  hideSoundEffect?: boolean;
  autoOnOffVideo?: boolean;
  forceUseSession?: MeetingSession;
  zoomPanPinch?: boolean | Omit<ReactZoomPanPinchProps, "ref">;
  callbacks?: Partial<MeetingSessionMediaPlayerEventListenerMap>;
  className?: string;
  style?: React.CSSProperties;
  renderCustomContainer?: PlayerContainerRenderer;
}

export interface MeetingSessionMediaPlayerRef {
  isPlayingSound: () => boolean;
  addEventListener: <K extends keyof MeetingSessionMediaPlayerEventListenerMap>(
    key: K,
    callback: MeetingSessionMediaPlayerEventListenerMap[K]
  ) => AddEventListernerResult<MeetingSessionMediaPlayerEventListenerMap, K>;
  removeEventListener: <K extends keyof MeetingSessionMediaPlayerEventListenerMap>(key: K, callback: MeetingSessionMediaPlayerEventListenerMap[K]) => void;
}

const MeetingSessionMediaPlayer = React.forwardRef(
  (
    {
      meeting,
      sessionId,
      mediaSessionId,
      ovenMediaSession,
      muted,
      hideSoundEffect,
      autoOnOffVideo = false,
      forceUseSession,
      zoomPanPinch,
      callbacks,
      className,
      style,
      renderCustomContainer,
      nativeProps,
    }: MeetingSessionMediaPlayerProps,
    forwardRef: ForwardedRef<MeetingSessionMediaPlayerRef>
  ) => {
    const eventListenersRef = useEventListenersRef<keyof MeetingSessionMediaPlayerEventListenerMap, MeetingSessionMediaPlayerEventListenerMap>();
    const callbacksRef = useRefAutoUpdate(callbacks);
    const [media, setMedia] = useState<MediaStream | undefined>(ovenMediaSession?.stream);
    const [videoIsEnabled, enableVideo] = useState(false);
    const session = useMemo(() => forceUseSession ?? meeting.sessions?.find((s) => s.id === sessionId), [meeting, sessionId, forceUseSession]);
    const mediaSession = useMemo(() => session?.medias?.find((m) => m.id === mediaSessionId), [session, mediaSessionId]);

    const [settings] = useSettings();
    const subscribptionsAPI = useOvenMediaVideoSubscriptions();

    const playerRef = useRef<MeetingSessionMediaStreamPlayerRef>(null);

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const checkVideo = (session: OvenMediaReceiverWebRTCSession) => {
      enableVideo(OvenMediaSessionService.videoIsEnabled(session));
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------   EVENTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    const triggerEvent = <K extends keyof MeetingSessionMediaPlayerEventListenerMap>(
      key: K,
      ...args: Parameters<MeetingSessionMediaPlayerEventListenerMap[K]>
    ) => {
      (callbacksRef.current?.[key] as any)?.call(null, ...args);
      eventListenersRef.current?.triggerEvent(key, ...args);
    };

    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useImperativeHandle(
      forwardRef,
      () => ({
        isPlayingSound: () => (playerRef.current?.isPlayingSound() ?? false),
        addEventListener: <K extends keyof MeetingSessionMediaPlayerEventListenerMap>(key: K, callback: MeetingSessionMediaPlayerEventListenerMap[K]) =>
          eventListenersRef.current?.addEventListener(key, callback),
        removeEventListener: <K extends keyof MeetingSessionMediaPlayerEventListenerMap>(key: K, callback: MeetingSessionMediaPlayerEventListenerMap[K]) =>
          eventListenersRef.current?.removeEventListener(key, callback),
      }),
      []
    );

    useEffect(() => {
      if (!ovenMediaSession) return;
      if (!autoOnOffVideo) return;
      subscribptionsAPI.update([{ session: ovenMediaSession, active: true }]);
      return () => subscribptionsAPI.reset();
    }, [ovenMediaSession]);

    useEffect(() => {
      if (!ovenMediaSession) return;
      const streamListener = ovenMediaSession.addEventListener("stream", (media: MediaStream) => setMedia(media));
      setMedia(ovenMediaSession.stream);
      checkVideo(ovenMediaSession);
      const tracksListener = ovenMediaSession.addEventListener("tracks", () => checkVideo(ovenMediaSession));
      return () => {
        streamListener.flush();
        tracksListener.flush();
      };
    }, [ovenMediaSession]);

    useEffect(() => {
      return () => subscribptionsAPI.reset();
    }, []);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    if (ovenMediaSession?.data.mediaSession && mediaSession?.id !== ovenMediaSession?.data.mediaSession.id) {
      // console.log("INTERNAL error: ", ovenMediaSession?.data.mediaSession && mediaSession?.id !== ovenMediaSession?.data.mediaSession.id, ovenMediaSession?.data.mediaSession, mediaSession)
      return null; // internal error
    }

    const renderContent = () => {
      const sessionIds = `User: ${session?.user?.id}\nSession: ${session?.id}\nMedia: ${mediaSession?.id}`;
      return (
        <MeetingSessionMediaStreamPlayer
          meeting={meeting}
          sessionId={sessionId}
          mediaSessionId={mediaSessionId}
          mediaStream={media}
          videoIsDisabled={!videoIsEnabled}
          muted={muted}
          hideSoundEffect={hideSoundEffect}
          forceUseSession={forceUseSession}
          zoomPanPinch={zoomPanPinch}
          callbacks={{
            playingSound: (playing) => {
              callbacks?.playingSound?.(playing);
              triggerEvent("playingSound", playing);
            },
          }}
          renderCustomContainer={renderCustomContainer}
          {...nativeProps}
          className={className}
          style={style}
          ref={playerRef}
        >
          {/* FOR DEV */}
          <div
            style={{
              position: "absolute",
              top: "5px",
              right: "20px",
              left: "5px",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              zIndex: 1,
              gap: "5px",
            }}
          >
            {settings?.debug?.displayOutgoingStreamStats || settings?.debug?.displaySessionIds ? (
              <WebrtcSessionStats
                session={settings?.debug?.displayOutgoingStreamStats ? ovenMediaSession : undefined}
                style={{ width: "100%", color: "white", background: "#00000088", padding: "5px", borderRadius: "5px" }}
              >
                {settings?.debug?.displaySessionIds && (
                  <div
                    style={{
                      width: "100%",
                      color: "white",
                      background: "#00000088",
                      cursor: "pointer",
                      padding: "5px",
                      fontSize: "10px",
                      whiteSpace: "pre",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      borderRadius: "5px",
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(sessionIds);
                        toastSuccess(`Copié dans le presse-papier...`);
                      } catch (err) {
                        toastError(`Impossible de copier les données dans le presse-papier: ${err}`);
                      }
                    }}
                  >
                    {sessionIds}
                  </div>
                )}
              </WebrtcSessionStats>
            ) : null}
          </div>
        </MeetingSessionMediaStreamPlayer>
      );
    };

    return renderContent();
  }
);

export default MeetingSessionMediaPlayer;
